import { Typography } from '@mui/material';
import { PieChart as MuiPieChart } from '@mui/x-charts/PieChart';
import React, { useEffect, useRef, useState } from 'react';

import { hexToRGBA } from '../BarChart';
import EnhacedBox from '../Box';

interface SemiPieChartProps {
  data: {
    id: string;
    label: string;
    value: number;
  }[];
  height?: number;
  width?: number;
  title?: string;
  icon?: React.ReactNode;
  color: string;
}

const SemiPieChart = ({ data, title, icon, color }: SemiPieChartProps) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const updateDimensions = () => {
      if (elementRef.current) {
        const { width } = elementRef.current.getBoundingClientRect();
        setHeight(width * 0.5);
        setWidth(width - 40);
      }
    };

    updateDimensions();

    window.addEventListener('resize', updateDimensions);

    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  return (
    <EnhacedBox small ref={elementRef}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 16,
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: hexToRGBA(color, 0.12),
          }}
        >
          {icon}
        </div>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            fontSize: '1.1rem',
          }}
        >
          {title}
        </Typography>
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
          minHeight: height,
        }}
      >
        <MuiPieChart
          series={[
            {
              data,
              innerRadius: 30,
              outerRadius: height ? 100 : 80,
              paddingAngle: 5,
              cornerRadius: 5,
              startAngle: -90,
              endAngle: 90,
              cx: width ? width / 2 : undefined,
              cy: height ? height - height * 0.35 : undefined,
              highlightScope: { fade: 'global', highlight: 'item' },
              faded: {
                innerRadius: 30,
                additionalRadius: -30,
                color: 'gray',
              },
              valueFormatter: (object) => `${object.value}`,
            },
          ]}
          height={height}
          width={width}
          margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
          slotProps={{
            legend: {
              direction: 'row',
              position: { vertical: 'bottom', horizontal: 'middle' },
              itemMarkWidth: 12,
              itemMarkHeight: 12,
              labelStyle: { fontSize: '14px' },
              padding: 10,
              itemGap: 12,
            },
          }}
        />
      </div>
    </EnhacedBox>
  );
};

export default SemiPieChart;
